import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	BFFConsumerEnum,
	BFFProvider,
	RequestStatus,
	useBFFDataProvider,
} from '@atlassian/navigation-bff-data-provider';
import {
	ERROR_LOCATION__GLOBAL_SHORTCUTS,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';

import type {
	fnGetFilteredProductLinks,
	GlobalShortcutsProps,
	MappedGlobalShortcutsItem,
} from '../types';
import { GlobalShortcutsWithIntlProvider } from '../utils/locale';
import { UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME } from '../utils/ufo/constants';
import { useGlobalShortcutsData } from '../utils/use-global-shortcuts-data';

import { DragAndDropOptionsProvider } from './drag-and-drop-options-provider';
import { ShortcutMenuItem } from './shortcut-menu-item';
import { ViewAllAppsItem } from './view-all-apps-item';

export function GlobalShortcuts(props: GlobalShortcutsProps) {
	return (
		<UFOSegment name={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}>
			<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SHORTCUTS}>
				<GlobalShortcutsInner {...props} />
			</BFFProvider>
		</UFOSegment>
	);
}

function GlobalShortcutsInner(props: GlobalShortcutsProps) {
	const { cloudId, forceStatic, product, shortcutItems: rawShortcutItems } = props;
	const { handleError } = useNavBoundaryContext({
		location: ERROR_LOCATION__GLOBAL_SHORTCUTS,
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product },
		disableAnalytics: true,
	});

	const shortcutItems = useGlobalShortcutsData({
		shortcutsConfig: rawShortcutItems,
		cloudId,
		isShortcutsStatic: forceStatic,
		onError: handleError,
		product,
	});

	return (
		<GlobalShortcutsWithIntlProvider>
			<NavigationAnalyticsContext data={{ product: props?.product }}>
				<GlobalShortcutsMenu {...props} shortcutItems={shortcutItems} />
			</NavigationAnalyticsContext>
		</GlobalShortcutsWithIntlProvider>
	);
}

function GlobalShortcutsMenu({
	testId,
	shortcutItems = [],
	cloudId,
	product,
	onUpdateGetFilteredProductLinks,
	enableDragAndDrop,
	viewAllAppsConfig = {},
	openInSameTab,
}: GlobalShortcutsProps) {
	const onUpdateGetFilteredProductLinksRef = useRef<any>(onUpdateGetFilteredProductLinks);
	onUpdateGetFilteredProductLinksRef.current = onUpdateGetFilteredProductLinks;

	const { status, productLinks } = useBFFDataProvider({
		request: {},
		cloudId,
		forceStatic: true,
		product,
	});

	const productLinksRef = useRef(productLinks);
	useEffect(() => {
		productLinksRef.current = productLinks;
	}, [productLinks]);

	const getBffProductLinksForKey = useCallback<fnGetFilteredProductLinks>(
		(apsKey: string) => ({
			links: productLinksRef.current[apsKey] ?? [],
			status,
		}),
		[status],
	);

	const mappedShortcutItems: Array<MappedGlobalShortcutsItem> = useMemo(() => {
		return shortcutItems.map((item, index) => {
			let key = (item.type === 'link' && item.apsKey) || `shortcut-${index}`;
			if (item.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
				key = item?.apsKey ? item.apsKey : `shortcut-${index}`;
			}

			return {
				...item,
				key,
			};
		});
	}, [shortcutItems]);

	useEffect(() => {
		if (onUpdateGetFilteredProductLinksRef.current) {
			onUpdateGetFilteredProductLinksRef.current(getBffProductLinksForKey);
		}
	}, [getBffProductLinksForKey]);

	const createLinkItem = useCallback(
		(shortcut: MappedGlobalShortcutsItem) => {
			if (status?.toLowerCase() === RequestStatus.COMPLETE) {
				if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
					return (
						<ShortcutMenuItem
							key={shortcut.key}
							shortcut={shortcut}
							dragAndDropEnabled={enableDragAndDrop}
							status={status}
							isInMoreMenu={false}
						/>
					);
				}

				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						openInSameTab={openInSameTab}
						status={status}
						product={product}
					/>
				);
			}

			if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						dragAndDropEnabled={enableDragAndDrop}
						status={status}
					/>
				);
			}

			return (
				<ShortcutMenuItem
					key={shortcut.key}
					shortcut={shortcut}
					status={status}
					product={product}
					openInSameTab={openInSameTab}
				/>
			);
		},
		[status, product, openInSameTab, enableDragAndDrop],
	);

	const shortcutList = (
		<MenuList>
			{mappedShortcutItems?.map(createLinkItem)}
			{viewAllAppsConfig?.path && <ViewAllAppsItem {...viewAllAppsConfig} product={product} />}
		</MenuList>
	);

	return (
		<Stack testId={testId}>
			{enableDragAndDrop ? (
				<DragAndDropOptionsProvider enabled={enableDragAndDrop}>
					{shortcutList}
				</DragAndDropOptionsProvider>
			) : (
				shortcutList
			)}
		</Stack>
	);
}
